import { useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Check, Utensils, Calendar, Loader2, Users, Plus, Minus, X } from "lucide-react";
import api from "../lib/api";

const DAYS = [
  { id: "23_agosto_cena", label: "23 Agosto", onlyCena: true },
  { id: "24_agosto", label: "24 Agosto" },
  { id: "25_agosto", label: "25 Agosto" },
  { id: "26_agosto", label: "26 Agosto" },
  { id: "27_agosto", label: "27 Agosto" },
  { id: "28_agosto", label: "28 Agosto" },
  { id: "29_agosto_pranzo", label: "29 Agosto", onlyPranzo: true },
];

const MEAL_TIMES = [
  { id: "pranzo", label: "Pranzo", icon: "🌞" },
  { id: "cena", label: "Cena", icon: "🌙" },
  { id: "entrambi", label: "Pranzo e Cena", icon: "🌞🌙", isBoth: true },
];

// Calculate price based on number of meals
const calculatePrice = (numMeals) => {
  if (numMeals <= 0) return 0;
  if (numMeals === 1) return 10;
  return numMeals * 7.5;
};

export default function SendMePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    phone: "",
    email: "",
    quantity: 1, // Numero di persone
  });
  
  // Array of meal selections: [{ day: "", meal_time: "" }, ...]
  // meal_time can be "pranzo", "cena", or "entrambi"
  const [meals, setMeals] = useState([{ day: "", meal_time: "" }]);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Calculate actual number of meals (entrambi counts as 2)
  const numMeals = useMemo(() => {
    return meals.reduce((count, meal) => {
      if (meal.meal_time === "entrambi") return count + 2;
      if (meal.meal_time) return count + 1;
      return count;
    }, 0);
  }, [meals]);

  const pricePerPerson = calculatePrice(numMeals);
  const totalPrice = pricePerPerson * formData.quantity;

  // Get available meal times based on selected day
  const getAvailableMealTimes = (selectedDay) => {
    const day = DAYS.find(d => d.id === selectedDay);
    if (!day) return [];
    if (day.onlyCena) return MEAL_TIMES.filter(t => t.id === "cena");
    if (day.onlyPranzo) return MEAL_TIMES.filter(t => t.id === "pranzo");
    // For days with both options, show all three choices
    return MEAL_TIMES;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMealChange = (index, field, value) => {
    setMeals(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-select meal_time if only one option
      if (field === "day") {
        const availableTimes = getAvailableMealTimes(value);
        if (availableTimes.length === 1) {
          updated[index].meal_time = availableTimes[0].id;
        } else if (!availableTimes.find(t => t.id === updated[index].meal_time)) {
          updated[index].meal_time = "";
        }
      }
      
      return updated;
    });
  };

  const addMeal = () => {
    if (meals.length < 14) { // Max 7 days x 2 meals = 14
      setMeals(prev => [...prev, { day: "", meal_time: "" }]);
    }
  };

  const removeMeal = (index) => {
    if (meals.length > 1) {
      setMeals(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Expand meals for backend (convert "entrambi" to two separate meals)
  const expandMealsForBackend = () => {
    const expanded = [];
    meals.forEach(meal => {
      if (meal.meal_time === "entrambi") {
        // Split into two meals
        expanded.push({ day: meal.day, meal_time: "pranzo" });
        expanded.push({ day: meal.day, meal_time: "cena" });
      } else if (meal.day && meal.meal_time) {
        expanded.push({ day: meal.day, meal_time: meal.meal_time });
      }
    });
    return expanded;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.surname || !formData.phone || !formData.email) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    // Check all meals are selected
    const incompleteMeals = meals.some(m => !m.day || !m.meal_time);
    if (incompleteMeals) {
      toast.error("Seleziona giorno e momento per tutti i pasti");
      return;
    }

    setIsSubmitting(true);
    try {
      // Expand meals (convert "entrambi" to two separate meals)
      const expandedMeals = expandMealsForBackend();
      
      // Build payload for backend
      const payload = {
        name: formData.name,
        surname: formData.surname,
        phone: formData.phone,
        email: formData.email,
        quantity: formData.quantity,
        package: `${expandedMeals.length}_meals`,
        meals: expandedMeals, // Array of all meal selections (expanded)
        // For backward compatibility, also send first two meals in old format
        day: expandedMeals[0]?.day || "",
        meal_time: expandedMeals[0]?.meal_time || "",
        day2: expandedMeals[1]?.day || "",
        meal_time2: expandedMeals[1]?.meal_time || "",
        total_price: totalPrice,
        price_per_person: pricePerPerson,
      };
      
      const bookingData = {
        numMeals: expandedMeals.length,
        quantity: formData.quantity,
        totalPrice,
        pricePerPerson,
        meals: expandedMeals,
      };
      
      await api.post("/sendme/bookings", payload);
      setConfirmedBooking(bookingData);
      setIsSuccess(true);
      toast.success("Prenotazione effettuata con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella prenotazione");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format meal time label for display
  const getMealTimeLabel = (mealTime) => {
    if (mealTime === "entrambi") return "Pranzo e Cena";
    return MEAL_TIMES.find(t => t.id === mealTime)?.label || mealTime;
  };

  if (isSuccess && confirmedBooking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-black" />
          </div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-4">Prenotazione Confermata!</h1>
          <p className="text-gray-300 mb-6">
            Grazie per la tua prenotazione. Riceverai una conferma via email.
          </p>
          <div className="bg-zinc-900 border border-yellow-400/30 rounded-xl p-6 mb-6">
            <p className="text-gray-400 text-sm mb-2">
              {confirmedBooking.numMeals} {confirmedBooking.numMeals === 1 ? "pasto" : "pasti"} x {confirmedBooking.quantity} {confirmedBooking.quantity === 1 ? "persona" : "persone"}
            </p>
            <p className="text-yellow-400 font-semibold text-lg">
              Totale da pagare in accettazione
            </p>
            <p className="text-4xl font-bold text-white mt-2">
              €{confirmedBooking.totalPrice.toFixed(2)}
            </p>
            
            {/* Show selected meals */}
            <div className="mt-4 pt-4 border-t border-zinc-700 text-left">
              <p className="text-gray-500 text-xs uppercase mb-2">Pasti selezionati:</p>
              <div className="space-y-1">
                {confirmedBooking.meals.map((meal, idx) => (
                  <div key={idx} className="text-gray-300 text-sm flex items-center gap-2">
                    <span className="text-yellow-400">{idx + 1}.</span>
                    {DAYS.find(d => d.id === meal.day)?.label} - {meal.meal_time === "pranzo" ? "Pranzo" : "Cena"}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-gray-500 text-sm">
            Il pagamento dei buoni pasto verrà effettuato in accettazione.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-b from-zinc-900 to-black py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <img 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_6292e840-6b8f-4ad9-807b-ea5b0fe1f71b/artifacts/tnz6qhzu_Sendme%20Conference%20Campdfgds.png" 
            alt="SendMe Conference Camp Logo" 
            className="h-28 mx-auto mb-6"
          />
          <h1 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">
            Prenota i Tuoi Pasti
          </h1>
          <p className="text-gray-400">
            23-29 Agosto 2026 • Centro Ghilghal, Mondragone
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Meal Selection */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
              <Utensils className="w-5 h-5" />
              Seleziona i Tuoi Pasti
            </h2>
            
            {/* Pricing Info */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                  <span className="text-gray-400">1 pasto: <span className="text-white font-semibold">€10</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                  <span className="text-gray-400">2+ pasti: <span className="text-white font-semibold">€7,50/pasto</span></span>
                </div>
              </div>
            </div>

            {/* Meals List */}
            <div className="space-y-3">
              {meals.map((meal, index) => (
                <div 
                  key={index} 
                  className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 relative"
                >
                  <div className="flex items-start gap-3">
                    {/* Meal Number */}
                    <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center text-black font-bold text-sm shrink-0">
                      {index + 1}
                    </div>
                    
                    {/* Selectors */}
                    <div className="flex-1 space-y-3">
                      {/* Day Selection */}
                      <select
                        value={meal.day}
                        onChange={(e) => handleMealChange(index, "day", e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                      >
                        <option value="">Seleziona giorno...</option>
                        {DAYS.map((day) => (
                          <option key={day.id} value={day.id}>
                            {day.label} {day.onlyCena ? "(solo Cena)" : day.onlyPranzo ? "(solo Pranzo)" : ""}
                          </option>
                        ))}
                      </select>
                      
                      {/* Meal Time Selection */}
                      {meal.day ? (
                        <div className="flex flex-wrap gap-2">
                          {getAvailableMealTimes(meal.day).map((time) => (
                            <button
                              key={time.id}
                              type="button"
                              onClick={() => handleMealChange(index, "meal_time", time.id)}
                              className={`py-2.5 px-4 rounded-lg font-medium transition-all text-sm flex items-center justify-center gap-1 ${
                                time.isBoth ? "flex-grow sm:flex-grow-0" : "flex-1"
                              } ${
                                meal.meal_time === time.id 
                                  ? time.isBoth 
                                    ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black" 
                                    : "bg-yellow-400 text-black" 
                                  : "bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700"
                              }`}
                            >
                              <span>{time.icon}</span> {time.label}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-zinc-800/50 rounded-lg py-2.5 px-3 text-gray-500 text-sm text-center border border-zinc-700">
                          Seleziona prima il giorno
                        </div>
                      )}
                    </div>
                    
                    {/* Remove Button */}
                    {meals.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMeal(index)}
                        className="w-8 h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center text-red-400 transition-colors shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Selection Summary */}
                  {meal.day && meal.meal_time && (
                    <div className="flex items-center gap-2 text-green-400 text-xs mt-3 ml-11">
                      <Check className="w-3 h-3" />
                      <span>
                        {DAYS.find(d => d.id === meal.day)?.label} - {getMealTimeLabel(meal.meal_time)}
                        {meal.meal_time === "entrambi" && <span className="text-gray-500 ml-1">(2 pasti)</span>}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Meal Button */}
            <Button
              type="button"
              onClick={addMeal}
              variant="outline"
              className="w-full border-dashed border-zinc-700 bg-transparent hover:bg-zinc-900 text-gray-400 hover:text-yellow-400 hover:border-yellow-400/50"
              disabled={meals.length >= 14}
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi un altro giorno
            </Button>
          </div>

          {/* Quantity Selection */}
          <div className="space-y-4 bg-zinc-900 rounded-xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Numero di Persone
            </h2>
            <p className="text-gray-500 text-sm">
              Quante persone faranno {numMeals === 1 ? "questo pasto" : `questi ${numMeals} pasti`}?
            </p>
            <div className="flex items-center justify-center gap-6">
              <button
                type="button"
                onClick={() => handleChange("quantity", Math.max(1, formData.quantity - 1))}
                className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:bg-zinc-700 transition-colors disabled:opacity-50"
                disabled={formData.quantity <= 1}
              >
                <Minus className="w-5 h-5 text-white" />
              </button>
              <div className="text-center">
                <div className="text-5xl font-bold text-white">{formData.quantity}</div>
                <div className="text-gray-400 text-sm mt-1">
                  {formData.quantity === 1 ? "persona" : "persone"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleChange("quantity", Math.min(50, formData.quantity + 1))}
                className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center hover:bg-yellow-500 transition-colors"
              >
                <Plus className="w-5 h-5 text-black" />
              </button>
            </div>
            
            {/* Price Summary */}
            {numMeals > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <div className="flex justify-between items-center text-sm text-gray-400 mb-2">
                  <span>{numMeals} {numMeals === 1 ? "pasto" : "pasti"} × €{(pricePerPerson / numMeals).toFixed(2)}</span>
                  <span>€{pricePerPerson.toFixed(2)}/persona</span>
                </div>
                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-400">
                    Totale ({formData.quantity} {formData.quantity === 1 ? "persona" : "persone"})
                  </span>
                  <span className="text-2xl font-bold text-yellow-400">
                    €{totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Personal Info */}
          <div className="space-y-4 bg-zinc-900 rounded-xl p-6 border border-zinc-800">
            <h2 className="text-xl font-bold text-yellow-400">I Tuoi Dati</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Nome *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="bg-black border-zinc-700 text-white focus:border-yellow-400"
                  placeholder="Mario"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Cognome *</Label>
                <Input
                  required
                  value={formData.surname}
                  onChange={(e) => handleChange("surname", e.target.value)}
                  className="bg-black border-zinc-700 text-white focus:border-yellow-400"
                  placeholder="Rossi"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Telefono *</Label>
                <Input
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="bg-black border-zinc-700 text-white focus:border-yellow-400"
                  placeholder="+39 333 1234567"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Email *</Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="bg-black border-zinc-700 text-white focus:border-yellow-400"
                  placeholder="mario@email.com"
                />
              </div>
            </div>
          </div>

          {/* Final Price Summary */}
          {numMeals > 0 && (
            <div className="bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 rounded-xl p-6 border border-yellow-400/50">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-gray-400 text-sm">Totale da pagare</div>
                  <div className="text-3xl font-bold text-yellow-400">€{totalPrice.toFixed(2)}</div>
                  <div className="text-gray-500 text-xs mt-1">
                    {numMeals} {numMeals === 1 ? "pasto" : "pasti"} × {formData.quantity} {formData.quantity === 1 ? "persona" : "persone"}
                  </div>
                </div>
                <div className="text-right text-gray-400 text-sm">
                  Il pagamento verrà<br />effettuato in accettazione
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || numMeals === 0}
            className="w-full h-14 bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg rounded-xl transition-all hover:scale-[1.02]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : (
              "Conferma Prenotazione"
            )}
          </Button>

          <p className="text-center text-gray-500 text-sm">
            Confermando accetti i termini e le condizioni dell'evento.
          </p>
        </form>
      </div>

      {/* WhatsApp Support Button - Right aligned */}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col items-center gap-2">
        <span className="bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          PER INFO
        </span>
        <a
          href="https://wa.me/393928553492?text=Ho%20bisogno%20di%20info%20sui%20buoni%20pasto"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-14 h-14 bg-[#25D366] rounded-full shadow-lg hover:scale-110 transition-transform"
          title="Contattaci su WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}
